import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import Intake from 'components/intake/Intake';
import { FormMeta, FormValues, PatientFormMode } from 'components/intake/types';
import ProtectedRoute from 'components/router/ProtectedRoute';
import { useEpisode } from 'context/episode';
import { FormHelpers } from 'context/form/types';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import Patient from 'models/Patient';
import { updatePatient } from 'services/api/patient';
import { useToastActions } from 'stores/toastStore';

export default function EditPatientPage() {
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { episode, invalidateData } = useEpisode();
  const { locationEpisode, invalidateData: invalidateLocationEpisode } = useSelectedLocationEpisode();
  const { profile } = useProfile();

  const { mutate } = useMutation({
    mutationFn: updatePatient,
  });

  if (!episode) return <></>;

  const handleSubmit = async (values: Partial<FormValues>, _meta, helpers: FormHelpers<FormValues, FormMeta>) => {
    helpers.setMeta('isSubmitting', true);

    const patientValues = Patient.fromFormValues(values).serialize();

    mutate(
      { ...patientValues, id: episode.patient.id },
      {
        onSuccess: (patient) => {
          addToast({ text: 'Patient successfully updated.' });
          invalidateData();
          invalidateLocationEpisode();
          navigate(`/patients/${patient.episodeId}`);
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again.' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  const initialValues = episode ? Patient.fromEpisodeData(episode).intakeFormValues() : {};

  return (
    <ProtectedRoute deny={!profile.canEditPatient(episode, locationEpisode)}>
      <Intake
        initialMeta={{
          isSubmitting: false,
          mode: PatientFormMode.Edit,
          patientHasActiveServiceRefusals: !!episode.locationEpisodes?.some((le) => le.hasActiveServiceRefusals),
          currentRehabState: episode.currentRehabState,
          latestLocationEpisodeIsArchived: !!episode.latestLocationEpisode?.archived,
          isManagerUser: profile.isManagerFor(episode),
        }}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        patient={episode.patient}
        resetOnInitialValuesChange
      />
    </ProtectedRoute>
  );
}
